

export namespace types {

    export type KeyPredicate = string | CompoundKey;
    export type CompoundKey = { [key: string]: string | number };
    export type EntityName = string;
    export type QueryOptions = string | { [key: string]: string };

    export interface CollectionNavigation {
        entityName: EntityName;
        key?: KeyPredicate;
    }

    export interface EntitySetIdentifier {
        entitySetName: EntityName;
        key?: KeyPredicate;
    }

    export interface EntitySet extends EntitySetIdentifier {
        property?: string | CollectionNavigation;
        queryOptions?: QueryOptions;
    }


    export type ResourcePath = EntitySet;

    export enum RetrieveRequestType {
        "ById",
        "ByAlternateKey",
        "ByFilter",
        "ByFetchXml"
    }

    export type ApiVersion = { apiVersion: "v8.2" | "v9.0" }

    /** This should replace the RetrieveRequest */
    export type RetrieveResourceRequest = ApiVersion & ResourcePath;


    export type DeleteRequest = ApiVersion & ResourcePath;


    export interface RetrieveRequest {
        type: RetrieveRequestType;
        entitySetName: string;
        apiVersion: "v8.2" | "v9.0";
    }
    export interface StoreRequest {
        type: RetrieveRequestType;
        entitySetName: string;
        apiVersion: "v8.2" | "v9.0";
        data: any;
    }

    export type StoreRequest2 = { data: any } & ApiVersion & ResourcePath;

    export interface StoreRequestByAlternateKeyRequest extends StoreRequest {
        type: RetrieveRequestType.ByAlternateKey;
        keyname: string;
        keyvalue: string | number;
    }

    interface InvokeActionRequestContent extends ApiVersion {
        actionName: string;
        data: any;
    }

    export type InvokeActionRequest = InvokeActionRequestContent & (InvokeUnBoundActionRequest | InvokeBoundActionRequest);

    export type InvokeUnBoundActionRequest = { isBound: false };

    export type InvokeBoundActionRequest = { isBound: true } & EntitySetIdentifier;


    export interface RetrieveByIdRequest extends RetrieveRequest {
        type: RetrieveRequestType.ById;
        id: string;
        columns?: string[];
    }

    export interface RetrieveByAlternateKeyRequest extends RetrieveRequest {
        type: RetrieveRequestType.ByAlternateKey;
        keyname: string;
        keyvalue: string | number;
        columns?: string[];
    }

    export interface FilterRequest extends RetrieveRequest {
        type: RetrieveRequestType.ByFilter;
        columns?: string[];
        filters?: FilterOption;
    }

    export interface FilterOption {
        logicalOperator: "and" | "or";
        conditions: string[];
    }

    export interface FetchXmlRequest extends RetrieveRequest {
        type: RetrieveRequestType.ByFetchXml;
        fetchXml: string;
    }

    export interface RetrieveResponse {

    }
    export interface StoreResponse {
        entityUri?: string;
    }
    export interface ActionResponse {

    }
    export interface DeleteResponse { }
}

function isEmptyKey(id: types.KeyPredicate): boolean {
    if (typeof id === "undefined" || id === null) {
        return true
    }

    if (typeof id === "string") {
        return id.length === 0;
    }

    return false;
}

function formatId(id: types.KeyPredicate) {
    if (isEmptyKey(id)) {
        return "";
    }

    if (typeof id === "string") {
        return `(${id})`;
    }

    const serialized = Object
        .keys(id)
        .map(key => `${key}=${formatValue(id[key])}`)
        .join(",");

    return `(${serialized})`;

    function formatValue(v: string | number): string {
        if (typeof v === "string") {
            return `'${v}'`;
        }
        return v.toString();
    }
}
function formatCollectionNavigation(nav: string | types.CollectionNavigation) {
    if (typeof nav === "string") {
        return nav;
    }
    if (nav === null || typeof nav === "undefined") {
        return "";
    }
    if (typeof nav !== "object") {
        console.warn("Unexpected navigation property in odata URI: ", nav);
        return String(nav);
    }
    return "/" + nav.entityName + formatId(nav.key);
}
function serializeQueryOptions(queryOptions: types.QueryOptions) {
    if (typeof (queryOptions) === "string") {
        return queryOptions;
    }
    throw new Error("Not yet supported");
}

function formatResourcePath(resourcePath: types.EntitySet): string {

    let url = resourcePath.entitySetName
        + formatId(resourcePath.key)
        + formatCollectionNavigation(resourcePath.property);

    if (typeof resourcePath.queryOptions !== "undefined" && resourcePath.queryOptions !== null) {
        url += "?" + serializeQueryOptions(resourcePath.queryOptions)
    }

    return url;
}

function isLegacyStoreRequest(storeRequest: types.StoreRequest | types.StoreRequest2): storeRequest is types.StoreRequest {
    return typeof (storeRequest as types.StoreRequest).type === "number";
}
function constructLegacyResourcePath(storeRequest: types.StoreRequestByAlternateKeyRequest): string {
    if (storeRequest.type !== types.RetrieveRequestType.ByAlternateKey) {
        throw new Error("Unknown RetrieveRequestType");
    }

    const keyvalue = typeof storeRequest.keyvalue === "string" ? `'${storeRequest.keyvalue}'` : storeRequest.keyvalue;
    const url = `${storeRequest.entitySetName}(${storeRequest.keyname}=${keyvalue})`;
    return url;
}


export class QueryClient {
    private clientUrl: string;

    private fallbackApiVersion: string = "v8.2";
    private defaultRequestInit: RequestInit = {
        headers: { "Content-Type": "application/json", "OData-MaxVersion": "4.0", "OData-Version": "4.0" },
        credentials: "include"
    };

    constructor(clientUrl: string) {
        this.clientUrl = clientUrl;
    }

    async invokeAction(request: types.InvokeActionRequest): Promise<types.ActionResponse> {
        let url = this.constructApiUrl(request.apiVersion);

        if (request.isBound) {
            url += formatResourcePath(request);
        }

        const fetchRequest: Request = new Request(url, {
            body: JSON.stringify(request.data),
            method: "POST"
        });

        const response = await fetch(fetchRequest, this.defaultRequestInit);

        if (response.status === 204) {
            return {};
        }

        return await response.json();
    }

    async delete(request: types.DeleteRequest): Promise<types.DeleteResponse> {
        let url = this.constructApiUrl(request.apiVersion);

        url += formatResourcePath(request);


        const fetchRequest: Request = new Request(url, {
            method: "DELETE"
        });

        const response = await fetch(fetchRequest, this.defaultRequestInit);

        if (response.status === 204) {
            return {};
        }

        return await response.json();
    }


    async store(storeRequest: types.StoreRequest | types.StoreRequest2): Promise<types.StoreResponse> {
        let url: string = this.constructApiUrl(storeRequest.apiVersion);
        let isUpsert = false;

        if (isLegacyStoreRequest(storeRequest)) {
            url += constructLegacyResourcePath(<types.StoreRequestByAlternateKeyRequest>storeRequest);
            isUpsert = true;
        } else {
            url += formatResourcePath(storeRequest);
            isUpsert = false === isEmptyKey(storeRequest.key);
        }

        const fetchRequest: Request = new Request(url, {
            body: JSON.stringify(storeRequest.data),
            method: isUpsert ? "PATCH" : "POST"
        });

        const response = await fetch(fetchRequest, this.defaultRequestInit);

        if (response.status === 204) {
            return {
                entityUri: response.headers.get("OData-EntityId")
            };
        }

        return await response.json();
    }


    execute(request: types.RetrieveRequest): Promise<types.RetrieveResponse> {

        switch (request.type) {
            case types.RetrieveRequestType.ById:
                return this.executeById(<types.RetrieveByIdRequest>request);
            case types.RetrieveRequestType.ByAlternateKey:
                return this.executeByAlternateKey(<types.RetrieveByAlternateKeyRequest>request);
            case types.RetrieveRequestType.ByFilter:
                return this.executeByFilter(<types.FilterRequest>request);
            case types.RetrieveRequestType.ByFetchXml:
                return this.executeByFetchXml(<types.FetchXmlRequest>request);
            default:
                throw new Error("Unknown RetrieveRequestType");
        }
    }

    async retrieve(request: types.RetrieveResourceRequest) {
        let url = this.constructApiUrl(request.apiVersion);
        url += formatResourcePath(request);

        const response = await fetch(url, this.defaultRequestInit);

        var responseContent = await response.json();

        return responseContent;
    }

    private constructApiUrl(apiVersion: string): string {
        if (!apiVersion) {
            apiVersion = this.fallbackApiVersion;
        }
        return this.clientUrl + `/api/data/${apiVersion}/`;
    }

    private executeById(request: types.RetrieveByIdRequest): Promise<types.RetrieveResponse> {
        let url = this.constructApiUrl(request.apiVersion);
        url += `${request.entitySetName}(${request.id})`;
        if (request.columns) {
            url += `?$select=${request.columns.join(",")}`;
        }

        return fetch(url, this.defaultRequestInit)
            .then(r => r.json());
    }

    private executeByAlternateKey(request: types.RetrieveByAlternateKeyRequest): Promise<types.RetrieveResponse> {
        let url = this.constructApiUrl(request.apiVersion);
        const keyvalue = typeof request.keyvalue === "string" ? `'${request.keyvalue}'` : request.keyvalue;
        url += `${request.entitySetName}(${request.keyname}=${keyvalue})`;

        if (request.columns) {
            url += `?$select=${request.columns.join(",")}`;
        }

        return fetch(url, this.defaultRequestInit)
            .then(r => r.json());
    }

    private executeByFilter(request: types.FilterRequest): Promise<types.RetrieveResponse> {
        //TODO
        let url = this.constructApiUrl(request.apiVersion);

        if (request.columns) {
            url += `?$select=${request.columns.join(",")}`;
        }
        return fetch(url + `${request.entitySetName}`, this.defaultRequestInit)
            .then(r => r.json());
    }

    private executeByFetchXml(request: types.FetchXmlRequest): Promise<types.RetrieveResponse> {
        const apiUrl = this.constructApiUrl(request.apiVersion);

        return fetch(apiUrl + `${request.entitySetName}?fetchXml=` + encodeURI(request.fetchXml), this.defaultRequestInit)
            .then(r => r.json());
    }
}



// WEBPACK FOOTER //
// ./ClientApp/app/services/query-proxy/index.ts